////////////////////////////////////////////////
//
// opener
//
////////////////////////////////////////////////
class YGQuickViewModalOpener extends HTMLElement {
  constructor() {
    super();

    this.onButtonClickHandler = this.onButtonClick.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector("button");

    if (this.button) {
      this.button.removeEventListener("click", this.onButtonClickHandler);
      this.button.addEventListener("click", this.onButtonClickHandler);
    }
  }

  async onButtonClick(event) {
    event.preventDefault();

    if (this.button?.getAttribute("aria-disabled") === "true") return;

    let modal = document.querySelector(this.dataset.modal);

    // 第一次加载 modal 容器（缓存到 body）
    if (!modal) {
      this.button.setAttribute("aria-disabled", true);
      this.button.classList.add("loading");
      this.button
        .querySelector(".loading-overlay__spinner")
        ?.classList.remove("hidden");

      try {
        const response = await fetch(
          `${Shopify.routes.root}?section_id=yg-quick-view-modal`
        );

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();

        const modalEl = getDomHtmlFromText(
          html,
          "yg-quick-view-modal"
        );

        document.body.appendChild(modalEl);

        modal = modalEl;
      } catch (e) {
        console.error('YGQuickView: failed to load modal container', e);
        this.button.removeAttribute("aria-disabled");
        this.button.classList.remove("loading");
        this.button
          .querySelector(".loading-overlay__spinner")
          ?.classList.add("hidden");
        return;
      }
    }

    // 立即打开弹窗（即使产品内容还没加载完）
    modal?.show(this.button);
  }

  disconnectedCallback() {
    this.button?.removeEventListener(
      "click",
      this.onButtonClickHandler
    );
  }
}


if (!customElements.get("yg-quick-view-modal-opener")) {
  customElements.define(
    "yg-quick-view-modal-opener",
    YGQuickViewModalOpener
  );
}
////////////////////////////////////////////////
//
// modal
//
////////////////////////////////////////////////

class YGQuickViewModal extends HTMLElement {
  constructor() {
    super();

    this.modalContent = this.querySelector('[id^="YGQuickViewInfo-"]');

    this.closeBtn = this.querySelector("[data-modal-close]");

    this.onClose = this.hide.bind(this);

    this._requestController = null;
    this._deferredImageTask = null;
    this._hideTimer = null;
  }

  ////////////////////////////////////////
  // lifecycle
  ////////////////////////////////////////

  connectedCallback() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", this.onClose);
    }

    this.addEventListener("click", (e) => {
      if (e.target === this) {
        this.hide();
      }
    });

    document.addEventListener("keyup", (e) => {
      if (e.key === "Escape") {
        this.hide();
      }
    });
  }

  disconnectedCallback() {
    if (this.closeBtn) {
      this.closeBtn.removeEventListener("click", this.onClose);
    }

    this._requestController?.abort();
    this.cancelDeferredImageLoading();
  }

  ////////////////////////////////////////
  // open
  ////////////////////////////////////////

  show(opener) {
    if (this._hideTimer) {
      window.clearTimeout(this._hideTimer);
      this._hideTimer = null;
    }

    this.opener = opener;

    document.documentElement.classList.add("overflow-hidden");

    this.classList.add("is-opening");

    this.removeAttribute("hidden");

    ////////////////////////////////////////
    // 立即显示弹窗壳层（容器+背景），内容异步填充
    ////////////////////////////////////////
    requestAnimationFrame(() => {
      this.classList.add("open");
      this.classList.remove("is-opening");
    });

    ////////////////////////////////////////
    // 缓存命中：相同产品 URL 直接显示，不重复请求
    ////////////////////////////////////////
    const url = this.buildQuickViewUrl(opener);

    if (this._cacheKey === url && this._cachedHtml) {
      // 已经加载过同一产品，直接渲染缓存
      this._renderProductFromHtml(this._cachedHtml);
      this.setLoading(false);
      return;
    }

    // 先使用商品卡已有数据绘制可见内容，完整产品数据在后台加载
    this.setLoading(true);
    this.renderInstantPreview(opener);

    ////////////////////////////////////////
    // 调用 yg 模板
    ////////////////////////////////////////

    this._requestController?.abort();
    const requestController = new AbortController();
    this._requestController = requestController;

    fetch(url, { signal: requestController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })

      .then((html) => {
        // 缓存
        this._cacheKey = url;
        this._cachedHtml = html;
        this._renderProductFromHtml(html);
      })

      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error('YGQuickView: failed to load product', err);
        this.modalContent.innerHTML = "<div style='padding:40px;text-align:center;color:#999;'>Failed to load product. Please try again.</div>";
      })

      .finally(() => {
        if (this._requestController === requestController) {
          this.setLoading(false);
        }
      });
  }

  // 渲染产品 HTML（提取重复逻辑，方便缓存命中）
  _renderProductFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    this.productElement = doc.querySelector(".main-product-template-wrapper");

    if (!this.productElement) {
      console.warn("yg quickview template not found");
      this.modalContent.innerHTML = "<div style='padding:40px;text-align:center;color:#999;'>Product content unavailable.</div>";
      return;
    }

    this.preventDuplicatedIDs();

    this.prepareProgressiveImages();

    this.modalContent.innerHTML = this.productElement.innerHTML;
    this.syncSelectedVariantMedia();
    this.removeGalleryListSemantic();
    this.updateImageSizes();
    this.preventVariantURLSwitching();
    this.startProgressiveImageLoading();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.rehydrateScripts(this.modalContent);
        this.reinitializeContent();

        if (window.YGQuickProductSection && typeof window.YGQuickProductSection.init === 'function') {
          window.YGQuickProductSection.init(this.modalContent);
        }

        this.syncSelectedVariantMedia();
        this.initShopifyFeatures();
      });
    });
  }

  prepareProgressiveImages() {
    const mediaWrapper = this.productElement.querySelector(".yg-product-media-wrapper");

    if (!mediaWrapper) return;

    mediaWrapper.querySelector(".thumbs-swiper")?.remove();

    const primaryImage =
      mediaWrapper.querySelector('.gallery-swiper .swiper-slide[data-swiper-slide-index="0"] img') ||
      mediaWrapper.querySelector(".gallery-swiper .swiper-slide img") ||
      mediaWrapper.querySelector("img");

    const placeholder =
      "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";

    mediaWrapper.querySelectorAll("img").forEach((image) => {
      if (image === primaryImage) {
        image.loading = "eager";
        image.fetchPriority = "high";
        return;
      }

      if (image.hasAttribute("src")) {
        image.dataset.deferredSrc = image.getAttribute("src");
      }

      if (image.hasAttribute("srcset")) {
        image.dataset.deferredSrcset = image.getAttribute("srcset");
      }

      image.setAttribute("src", placeholder);
      image.removeAttribute("srcset");
      image.loading = "lazy";
      image.fetchPriority = "low";
    });
  }

  startProgressiveImageLoading() {
    const primaryImage =
      this.modalContent.querySelector('.gallery-swiper .swiper-slide[data-swiper-slide-index="0"] img') ||
      this.modalContent.querySelector(".gallery-swiper .swiper-slide img");

    const start = () => this.scheduleNextDeferredImage();

    if (!primaryImage || primaryImage.complete) {
      start();
      return;
    }

    primaryImage.addEventListener("load", start, { once: true });
    primaryImage.addEventListener("error", start, { once: true });
  }

  scheduleNextDeferredImage() {
    this.cancelDeferredImageLoading();

    const loadNext = () => {
      const image = this.modalContent.querySelector(
        "img[data-deferred-src], img[data-deferred-srcset]",
      );

      if (!image) return;

      if (image.dataset.deferredSrcset) {
        image.setAttribute("srcset", image.dataset.deferredSrcset);
        delete image.dataset.deferredSrcset;
      }

      if (image.dataset.deferredSrc) {
        image.setAttribute("src", image.dataset.deferredSrc);
        delete image.dataset.deferredSrc;
      }

      this.scheduleNextDeferredImage();
    };

    if ("requestIdleCallback" in window) {
      this._deferredImageTask = {
        type: "idle",
        id: window.requestIdleCallback(loadNext, { timeout: 1500 }),
      };
      return;
    }

    this._deferredImageTask = {
      type: "timeout",
      id: window.setTimeout(loadNext, 200),
    };
  }

  cancelDeferredImageLoading() {
    if (!this._deferredImageTask) return;

    if (this._deferredImageTask.type === "idle") {
      window.cancelIdleCallback(this._deferredImageTask.id);
    } else {
      window.clearTimeout(this._deferredImageTask.id);
    }

    this._deferredImageTask = null;
  }

  renderInstantPreview(opener) {
    if (!this.modalContent) return;

    const card = opener.closest(".card-wrapper");
    const cardImage = card?.querySelector(".card__media img, .card__inner img");
    const cardPrice = card?.querySelector(
      ".custom_card_price:not(.original-price), .price-item--sale:not(.original-price), .price-item--regular",
    );
    const title = opener.dataset.previewTitle || "";
    const price = cardPrice?.textContent?.trim() || opener.dataset.previewPrice || "";
    const imageUrl = cardImage?.currentSrc || cardImage?.src || opener.dataset.previewImage || "";
    const imageAlt = opener.dataset.previewImageAlt || title;
    const productUrl = opener.dataset.productUrl || "#";
    const addLabel = opener.dataset.previewAddLabel || "";
    const buyLabel = opener.dataset.previewBuyLabel || "";
    const detailsLabel = opener.dataset.previewDetailsLabel || "";

    this.modalContent.innerHTML = `
      <div class="yg-quick-view-preview" aria-busy="true">
        <div class="yg-quick-view-preview__media">
          ${imageUrl ? `<img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(imageAlt)}" width="1000" height="1000" loading="eager" fetchpriority="high">` : ""}
        </div>
        <div class="yg-quick-view-preview__info">
          <h2 class="yg-quick-view-preview__title">${this.escapeHtml(title)}</h2>
          <div class="yg-quick-view-preview__price">${this.escapeHtml(price)}</div>
          <div class="yg-quick-view-preview__field" aria-hidden="true">
            <span class="yg-quick-view-preview__line yg-quick-view-preview__line--label"></span>
            <span class="yg-quick-view-preview__line yg-quick-view-preview__line--input"></span>
          </div>
          <div class="yg-quick-view-preview__field" aria-hidden="true">
            <span class="yg-quick-view-preview__line yg-quick-view-preview__line--label"></span>
            <span class="yg-quick-view-preview__swatches">
              <i></i><i></i><i></i><i></i>
            </span>
          </div>
          <div class="yg-quick-view-preview__actions">
            <div class="yg-quick-view-preview__quantity" aria-hidden="true">
              <span>-</span><span>1</span><span>+</span>
            </div>
            <button class="yg-quick-view-preview__button yg-quick-view-preview__button--cart" type="button" disabled aria-disabled="true">${this.escapeHtml(addLabel)}</button>
            <button class="yg-quick-view-preview__button yg-quick-view-preview__button--buy" type="button" disabled aria-disabled="true">${this.escapeHtml(buyLabel)}</button>
          </div>
          <a class="yg-quick-view-preview__details" href="${this.escapeHtml(productUrl)}">${this.escapeHtml(detailsLabel)} <span aria-hidden="true">›</span></a>
        </div>
      </div>
    `;
  }

  escapeHtml(value) {
    const element = document.createElement("span");
    element.textContent = String(value || "");
    return element.innerHTML;
  }

  // 渲染骨架屏
  renderSkeleton() {
    if (!this.modalContent) return;
    this.modalContent.innerHTML = `
      <div class="yg-quick-view-skeleton" style="display:flex;gap:32px;width:100%;padding:0;align-items:stretch;min-height:520px;">
        <div class="yg-quick-view-skeleton__media" style="
          flex:0 0 55%;
          background:linear-gradient(90deg,#eee 0%,#f7f7f7 50%,#eee 100%);
          background-size:200% 100%;
          animation:ygSkelShimmer 1.4s infinite;
          border-radius:12px;
          min-height:520px;
        ">
          <div style="display:flex;flex-direction:column;gap:10px;padding:12px;position:absolute;bottom:12px;left:12px;right:12px;">
            <div style="width:100%;height:64px;background:rgba(255,255,255,0.5);border-radius:6px;background:linear-gradient(90deg,#e0e0e0 0%,#ededed 50%,#e0e0e0 100%);background-size:200% 100%;animation:ygSkelShimmer 1.4s infinite;"></div>
          </div>
        </div>
        <div class="yg-quick-view-skeleton__info" style="flex:1;display:flex;flex-direction:column;gap:14px;padding-top:8px;">
          <div style="width:75%;height:24px;background:linear-gradient(90deg,#eee 0%,#f7f7f7 50%,#eee 100%);background-size:200% 100%;animation:ygSkelShimmer 1.4s infinite;border-radius:6px;"></div>
          <div style="width:45%;height:16px;background:linear-gradient(90deg,#eee 0%,#f7f7f7 50%,#eee 100%);background-size:200% 100%;animation:ygSkelShimmer 1.4s infinite;border-radius:6px;"></div>
          <div style="width:55%;height:28px;background:linear-gradient(90deg,#eee 0%,#f7f7f7 50%,#eee 100%);background-size:200% 100%;animation:ygSkelShimmer 1.4s infinite;border-radius:6px;margin-top:6px;"></div>
          <div style="width:90%;height:80px;background:linear-gradient(90deg,#eee 0%,#f7f7f7 50%,#eee 100%);background-size:200% 100%;animation:ygSkelShimmer 1.4s infinite;border-radius:6px;margin-top:8px;"></div>
          <div style="width:100%;height:46px;background:linear-gradient(90deg,#eee 0%,#f7f7f7 50%,#eee 100%);background-size:200% 100%;animation:ygSkelShimmer 1.4s infinite;border-radius:99px;margin-top:8px;"></div>
          <div style="display:flex;gap:8px;margin-top:6px;">
            <div style="flex:1;height:52px;background:linear-gradient(90deg,#eee 0%,#f7f7f7 50%,#eee 100%);background-size:200% 100%;animation:ygSkelShimmer 1.4s infinite;border-radius:99px;"></div>
            <div style="flex:1;height:52px;background:linear-gradient(90deg,#eee 0%,#f7f7f7 50%,#eee 100%);background-size:200% 100%;animation:ygSkelShimmer 1.4s infinite;border-radius:99px;"></div>
          </div>
        </div>
      </div>
      <style>
        @keyframes ygSkelShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @media (max-width: 990px) {
          .yg-quick-view-skeleton {
            flex-direction: column !important;
          }
          .yg-quick-view-skeleton__media {
            flex: 0 0 auto !important;
            min-height: 360px !important;
          }
        }
      </style>
    `;
  }

  ////////////////////////////////////////
  // load product by handle
  ////////////////////////////////////////

  buildQuickViewUrl(opener) {
    const productUrl = opener.dataset.productUrl || "";
    const url = new URL(productUrl, window.location.origin);
    const variantId = opener.dataset.variantId;

    url.searchParams.set("view", "yg-quick-view");

    if (variantId) {
      url.searchParams.set("variant", variantId);
    }

    return url.toString();
  }

  loadProductByHandle(handle) {
    this.setLoading(true);

    let url = `${Shopify.routes.root}products/${handle}?view=yg-quick-view`;

    // 缓存命中
    if (this._cacheKey === url && this._cachedHtml) {
      this._renderProductFromHtml(this._cachedHtml);
      this.setLoading(false);
      return;
    }

    this.renderSkeleton();

    this._requestController?.abort();
    const requestController = new AbortController();
    this._requestController = requestController;

    fetch(url, { signal: requestController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        this._cacheKey = url;
        this._cachedHtml = html;
        this._renderProductFromHtml(html);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error('YGQuickView: failed to load product by handle', err);
        this.modalContent.innerHTML = "<div style='padding:40px;text-align:center;color:#999;'>Failed to load product. Please try again.</div>";
      })
      .finally(() => {
        if (this._requestController === requestController) {
          this.setLoading(false);
        }
      });
  }

  ////////////////////////////////////////
  // close
  ////////////////////////////////////////

  hide() {
    this._requestController?.abort();
    this.cancelDeferredImageLoading();
    this.setLoading(false);
    this.classList.remove("open");

    document.documentElement.classList.remove("overflow-hidden");

    this._hideTimer = window.setTimeout(() => {
      this.modalContent.innerHTML = "";

      this.setAttribute("hidden", "");
      this._hideTimer = null;
    }, 300);
  }

  ////////////////////////////////////////
  // loading
  ////////////////////////////////////////

  setLoading(state) {
    if (!this.opener) return;

    this.opener.toggleAttribute("aria-disabled", state);

    this.opener.classList.toggle("loading", state);

    const spinner = this.opener.querySelector(".loading-overlay__spinner");

    if (spinner) {
      spinner.classList.toggle("hidden", !state);
    }
  }

  reinitializeContent() {
    this.initSwipers();
    this.initCustomDropdowns();
    this.upgradeCustomElements();

    // 初始化 JudgeMe 评分星星图标
    if (window.Judgeme) {
      if (typeof window.Judgeme.boot === 'function') {
        window.Judgeme.boot();
      } else if (typeof window.Judgeme.init === 'function') {
        window.Judgeme.init();
      } else if (typeof window.Judgeme.CSSRewriter !== 'undefined') {
        window.Judgeme.CSSRewriter.rewrite();
      }
    }

    // 兜底：手动触发 CSS 重写类，强制显示星星
    const badge = this.modalContent.querySelector('.jdgm-preview-badge');
    if (badge) {
      badge.querySelectorAll('.jdgm-star').forEach(star => {
        star.classList.add('jdgm--rewriting');
        star.classList.remove('jdgm--rewriting');
      });
    }

    // 手动给 jdgm-star 嵌入 SVG 星星图标
    const stars = this.modalContent.querySelectorAll('.jdgm-star');
    if (stars.length) {
      const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
        <path d="M6.69489 0.234665C6.82502 -0.0782082 7.26824 -0.0782094 7.39837 0.234664L9.0733 4.26167C9.12816 4.39357 9.2522 4.48369 9.3946 4.49511L13.7421 4.84365C14.0799 4.87073 14.2168 5.29225 13.9595 5.5127L10.6471 8.35006C10.5387 8.443 10.4913 8.58882 10.5244 8.72777L11.5364 12.9702C11.615 13.2998 11.2564 13.5603 10.9673 13.3837L7.2452 11.1103C7.12329 11.0358 6.96997 11.0358 6.84806 11.1103L3.126 13.3837C2.83682 13.5603 2.47824 13.2998 2.55687 12.9702L3.56884 8.72777C3.60198 8.58882 3.5546 8.443 3.44611 8.35006L0.133782 5.5127C-0.123566 5.29225 0.0133956 4.87073 0.351169 4.84365L4.69866 4.49511C4.84106 4.48369 4.9651 4.39357 5.01996 4.26167L6.69489 0.234665Z" fill="#FF6A00"/>
      </svg>`;
      stars.forEach(star => {
        star.innerHTML = svgIcon;
      });
    }
  }

  ////////////////////////////////////////
  // Swiper
  ////////////////////////////////////////

  initSwipers() {
    const gallery = this.modalContent.querySelector('.gallery-swiper');

    if (!gallery) return;

    this.gallery_swiper = new Swiper(gallery, {
      observer: true,
      observeParents: true,
      slidesPerView: 1,
      effect: 'fade',
      speed: 500,
      navigation: {
        nextEl: gallery.querySelector('.swiper-button-next'),
        prevEl: gallery.querySelector('.swiper-button-prev'),
      },
    });
  }


  // 下拉列表
  initCustomDropdowns() {

    const dropdowns =
      this.modalContent.querySelectorAll(".custom-dropdown");
      if (!dropdowns.length) return;
      dropdowns.forEach((dropdown) => {
        if (dropdown.dataset.initialized) return;
        dropdown.dataset.initialized = "true";
    
        const trigger =
          dropdown.querySelector(".dropdown-trigger");
    
        const options =
          dropdown.querySelectorAll(".dropdown-option");
    
        const parentContainer =
          dropdown.closest(".product-form__input--dropdown");
    
        ////////////////////////////////////////
        // toggle
        ////////////////////////////////////////
    
        trigger?.addEventListener("click", (e) => {
    
          e.stopPropagation();
    
          const isOpen =
            dropdown.classList.contains("open");
    
          dropdowns.forEach((d) => {
    
            d.classList.remove("open");
    
            const p =
              d.closest(".product-form__input--dropdown");
    
            p?.classList.remove("is-active-dropdown");
    
          });
    
          if (!isOpen) {
    
            dropdown.classList.add("open");
    
            parentContainer?.classList.add(
              "is-active-dropdown"
            );
    
          }
    
        });
    
        ////////////////////////////////////////
        // option click
        ////////////////////////////////////////

        options.forEach((option) => {

          option.addEventListener("click", () => {

            const handle = option.getAttribute("data-handle");

            if (handle) {

              this.loadProductByHandle(handle);

            }

          });

        });
    
      });
  
    ////////////////////////////////////////
    // click outside
    ////////////////////////////////////////
  
    if (!this._dropdownDocHandler) {
  
      this._dropdownDocHandler = (e) => {
  
        dropdowns.forEach((dropdown) => {
  
          if (!dropdown.contains(e.target)) {
  
            dropdown.classList.remove("open");
  
            dropdown
              .closest(".product-form__input--dropdown")
              ?.classList.remove("is-active-dropdown");
  
          }
  
        });
  
      };
  
      document.addEventListener(
        "click",
        this._dropdownDocHandler
      );
  
    }
  
  }

  // 折扣
  upgradeCustomElements() {

    const elements =
      this.modalContent.querySelectorAll('yg-discount');
  
    elements.forEach(el => {
  
      customElements.upgrade(el);
  
    });
  
  }

  ////////////////////////////////////////
  // html inject
  ////////////////////////////////////////

  rehydrateScripts(el) {
    el.querySelectorAll("script")

      .forEach((oldScript) => {
        const script = document.createElement("script");

        [...oldScript.attributes].forEach((attr) =>
          script.setAttribute(attr.name, attr.value),
        );

        script.textContent = oldScript.textContent;

        oldScript.replaceWith(script);
      });

  }

  ////////////////////////////////////////
  // shopify helpers
  ////////////////////////////////////////

  initShopifyFeatures() {
    window.Shopify?.PaymentButton?.init();

    window.ProductModel?.loadShopifyXR();
  }

  preventVariantURLSwitching() {
    this.modalContent

      .querySelectorAll("variant-radios,variant-selects")

      .forEach((el) => el.setAttribute("data-update-url", "false"));
  }

  syncSelectedVariantMedia() {
    const mediaWrapper = this.modalContent.querySelector(".yg-product-media-wrapper");

    if (!mediaWrapper) return;

    const selectedMediaUrl = this.getSelectedVariantMediaUrl();

    if (!selectedMediaUrl) return;

    const firstGallerySlide =
      mediaWrapper.querySelector('.gallery-swiper .swiper-slide[data-swiper-slide-index="0"]') ||
      mediaWrapper.querySelector(".gallery-swiper .swiper-slide");

    const updateImage = (img) => {
      img.src = selectedMediaUrl;
      img.srcset = `${selectedMediaUrl} 1000w`;
    };

    const galleryImages = mediaWrapper.querySelectorAll(
      '.gallery-swiper .swiper-slide[data-swiper-slide-index="0"] img',
    );

    if (galleryImages.length) {
      galleryImages.forEach(updateImage);
    } else {
      firstGallerySlide.querySelectorAll("img").forEach(updateImage);
    }

    const gallerySwiper = this.gallery_swiper || mediaWrapper.querySelector(".gallery-swiper")?.swiper;

    gallerySwiper?.update?.();
    gallerySwiper?.slideTo?.(0, 0);
  }

  getSelectedVariantMediaUrl() {
    const variantId = this.modalContent.querySelector('input[name="id"]')?.value;

    if (!variantId) return null;

    const variantDataScript = this.modalContent.querySelector(
      "variant-radios script[type='application/json'], variant-selects script[type='application/json']",
    );

    if (!variantDataScript?.textContent) return null;

    try {
      const variants = JSON.parse(variantDataScript.textContent);
      const variant = variants.find((item) => String(item.id) === String(variantId));
      const imageSrc = variant?.featured_image?.src;

      return imageSrc ? this.normalizeImageUrl(imageSrc) : null;
    } catch (error) {
      console.warn("Unable to parse quick view variant media", error);
      return null;
    }
  }

  normalizeImageUrl(src) {
    if (src.startsWith("//")) {
      return `${window.location.protocol}${src}`;
    }

    if (src.startsWith("/")) {
      return `${window.location.origin}${src}`;
    }

    return src;
  }

  preventDuplicatedIDs() {
    const sectionId = this.productElement.dataset.section;

    if (!sectionId) return;

    this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(
      sectionId,

      `yg-${sectionId}`,
    );

    this.productElement

      .querySelectorAll("variant-selects,variant-radios")

      .forEach((el) => (el.dataset.originalSection = sectionId));
  }

  removeGalleryListSemantic() {
    const galleryList = this.modalContent.querySelector(
      '[id^="Slider-Gallery"]',
    );

    if (!galleryList) return;

    galleryList.setAttribute("role", "presentation");

    galleryList

      .querySelectorAll('[id^="Slide-"]')

      .forEach((li) => li.setAttribute("role", "presentation"));
  }

  updateImageSizes() {
    const product = this.modalContent.querySelector(".product");

    if (!product) return;

    const images = product.querySelectorAll(".product__media img");

    images.forEach((img) =>
      img.setAttribute(
        "sizes",

        "(min-width: 1000px) 715px, 100vw",
      ),
    );
  }
}
if (!customElements.get("yg-quick-view-modal")) {
  customElements.define("yg-quick-view-modal", YGQuickViewModal);
}
